import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { EmptyPage } from '../other/EmptyPage';
import { Tooltip } from 'react-tooltip';
import ReactPaginate from 'react-paginate';
// import {activateUser} from "../../services/user";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Select from 'react-select';
import { usePermission } from '../../container/permissionContext';
import useOutsideClick from '../../hook/useOutsideClick';
import { DATE_FORMAT } from '../../constants';
import { dateToISOString } from '../../utility/dates/dateToISOString';
import { utcToLocalDateFormatter } from '../../utility/dates/utcToLocalDate';
import { formatCalendarDate } from '../../utility/dates/formatCalendarDate';
import DateCalendar from '../calendars/DateCalendar/DateCalendar';
import { exportUsersService } from '../../services/user';
import { isResponseOk } from '../../utility/isResponseOk';
import { localToUTCStringForDateRange } from '../../utility/dates/localToUTCForDateRange';
import { getDefaultUserListFilters } from '../../utility/users/user-list-filters';
import { allRolesOption, userRegistrationStatusOptions, userStatusOptions } from '../../constants/users/options';

function UserTab({
  users,
  openImport,
  openUpdateModal,
  openAddModal,
  totalPage,
  page,
  handlePageClick,
  openAddAttachModal,
  onUpdate,
  isShortTable,
  totalUser,
  handelFilter,
  filtersUser,
  facilities,
  positions,
  userGroup,
  roles,
  onSearch,
}) {
  const { id } = useParams();
  const [usersList, setUsersList] = useState(users);
  const [search, setSearch] = useState('');
  // filters hold the plain values the API expects (ids and strings), the parent owns them and restores them from the URL
  const [filters, setFilters] = useState(() => (filtersUser ? { ...getDefaultUserListFilters(), ...filtersUser } : {}));
  const [filtersIsOpen, setFiltersIsOpen] = useState(false);
  const [ordering, setOrdering] = useState(filtersUser?.ordering || null);
  const [facilitiesOption, setFacilitiesOption] = useState([]);
  const [positionsOption, setPositionsOption] = useState([]);
  const [userGroupOption, setUserGroupOption] = useState([]);
  const [roleOption, setRoleOption] = useState([]);
  const [isExportingUsers, setIsExportingUsers] = useState(false);
  const navigate = useNavigate();
  const [column, setColumn] = useState([]);
  const location = useLocation();

  const [startDate, setStartDate] = useState(() => (filtersUser?.date_added_after ? new Date(filtersUser.date_added_after) : null));
  const [endDate, setEndDate] = useState(() => (filtersUser?.date_added_before ? new Date(filtersUser.date_added_before) : null));
  const { isEmployee } = usePermission();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const ref = useRef();
  const closeDropdown = () => {
    setIsDropdownOpen(false);
  };
  useOutsideClick(ref, closeDropdown);

  const handleDateChange = (date) => {
    setStartDate(date[0]);
    setEndDate(date[1]);
  };

  // a range is only applied once both of its ends are picked
  useEffect(() => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      date_added_before: endDate ? dateToISOString(endDate) : '',
      date_added_after: endDate && startDate ? dateToISOString(startDate) : '',
    }));
  }, [endDate, startDate]);

  useEffect(() => {
    if (isShortTable) {
      setColumn([
        {
          name: 'User Name',
          total: totalUser,
        },
        {
          name: 'Position',
        },
        {
          name: 'User Group',
        },
        {
          name: 'Date Added',
        },
      ]);
    } else if (isEmployee()) {
      setColumn([
        {
          name: 'User Name',
          total: totalUser,
        },
        {
          name: 'Facility',
        },
        {
          name: 'Position',
        },
        {
          name: 'User Group',
        },
        {
          name: 'Date Added',
          styles: { maxWidth: '150px', width: '150px' },
        },
        {
          name: 'Date Enrolled',
          styles: { maxWidth: '150px', width: '150px' },
          sortKey: 'activated_at',
        },
        {
          name: 'Hire Date',
          styles: { maxWidth: '150px', width: '150px' },
        },
      ]);
    } else {
      setColumn([
        {
          name: 'User Name',
          total: totalUser,
        },
        {
          name: 'Facility',
        },
        {
          name: 'Position',
        },
        {
          name: 'User Group',
        },
        {
          name: 'Date Added',
          styles: { maxWidth: '150px', width: '150px' },
        },
        {
          name: 'Date Enrolled',
          styles: { maxWidth: '150px', width: '150px' },
          sortKey: 'activated_at',
        },
        {
          name: 'Hire Date',
          styles: { maxWidth: '150px', width: '150px' },
        },
      ]);
    }
    // eslint-disable-next-line
  }, [search, users]);

  useEffect(() => {
    setUsersList(users);
  }, [users]);

  useEffect(() => {
    if (roles) {
      setRoleOption(roles.map((item) => ({ value: item.id, label: item.name })));
    }
  }, [roles]);
  useEffect(() => {
    if (facilities) {
      setFacilitiesOption(
        facilities.map((item) => ({
          value: item.id,
          label: `${item.name}${item.deleted_at ? ' (Deactivated)' : ''}`,
        })),
      );
    }
  }, [facilities]);

  useEffect(() => {
    if (positions) {
      setPositionsOption(positions.map((item) => ({ value: item.id, label: item.name })));
    }
  }, [positions]);
  useEffect(() => {
    if (userGroup) {
      setUserGroupOption(userGroup.map((item) => ({ value: item.id, label: `${item.name}` })));
    }
  }, [userGroup]);

  useEffect(() => {
    if (filtersUser) {
      const newFilter = { ...filters };

      if (ordering) {
        newFilter.ordering = ordering;
      } else {
        delete newFilter.ordering;
      }

      handelFilter(newFilter);
    }

    // eslint-disable-next-line
  }, [filters, ordering]);

  // filters keep ids only, the matching options carry the labels the selects display
  const selectedFacilitiesOptions = useMemo(
    () => facilitiesOption.filter((option) => filters.facilities_ids?.includes(option.value)),
    [facilitiesOption, filters.facilities_ids],
  );
  const selectedPositionsOptions = useMemo(
    () => positionsOption.filter((option) => filters.positions_ids?.includes(option.value)),
    [positionsOption, filters.positions_ids],
  );
  const selectedUserGroupsOptions = useMemo(
    () => userGroupOption.filter((option) => filters.user_groups_ids?.includes(option.value)),
    [userGroupOption, filters.user_groups_ids],
  );
  const roleOptions = useMemo(() => [allRolesOption, ...roleOption], [roleOption]);
  const selectedRoleOption = Array.isArray(filters.role_ids)
    ? allRolesOption
    : roleOption.find((option) => option.value === filters.role_ids);

  const toggleSort = (key) => {
    setOrdering((current) => {
      if (current === key) return `-${key}`;
      if (current === `-${key}`) return null;
      return key;
    });
  };

  const onFilter = (value, key) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [key]: value,
    }));
  };

  const onMultiSelectFilter = (selectedOptions, key) =>
    onFilter(
      (selectedOptions || []).map((option) => option.value),
      key,
    );

  const onSelectFilter = (selectedOption, key) => onFilter(selectedOption ? selectedOption.value : getDefaultUserListFilters()[key], key);

  const clearFilter = () => {
    setFilters(getDefaultUserListFilters());

    setStartDate(null);
    setEndDate(null);
  };
  // const handleActivateUser = (id) => {
  //     activateUser(id).then(res => onUpdate()).catch(e => console.log(e))
  // }
  //
  // const handleDeactivateUser = (id) => {
  //     deactivateUser(id).then(res => onUpdate()).catch(e => console.log(e))
  // }

  const handlePrintUsers = useCallback(async () => {
    try {
      setIsExportingUsers(true);
      console.log(filters);

      const payload = {
        company_id: id,
      };

      if (filters.date_added_after) {
        payload.date_added_after = localToUTCStringForDateRange(filters.date_added_after, 'start');
      }

      if (filters.date_added_before) {
        payload.date_added_before = localToUTCStringForDateRange(filters.date_added_before, 'end');
      }

      if (filters.registration_status) {
        payload.registration_status = filters.registration_status;
      }

      if (filters.status) {
        payload.status = filters.status;
      }

      if (filters.role_ids) {
        payload.role_ids = filters.role_ids;
      }

      if (filters.user_groups_ids) {
        payload.user_groups_ids = filters.user_groups_ids;
      }

      if (filters.facilities_ids) {
        payload.facilities_ids = filters.facilities_ids;
      }

      if (filters.positions_ids) {
        payload.positions_ids = filters.positions_ids;
      }

      if (filters.search) {
        payload.search = filters.search;
      }

      const response = await exportUsersService(payload);

      if (isResponseOk(response)) {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'users.xlsx');
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
      }
      setIsExportingUsers(false);
    } catch (e) {
      console.log(e);
      setIsExportingUsers(false);
    }
  }, [id, filters, setIsExportingUsers]);

  return (
    <>
      <div className="library-filter">
        {!filtersUser && (
          <div className="library-filter-search form-control-item">
            <input
              type="text"
              className="form-control"
              value={search}
              onChange={(e) => {
                onSearch(e.target.value);
                setSearch(e.target.value);
              }}
              placeholder="Search"
            />
            <i className="icon-search"></i>
          </div>
        )}
        {filtersUser && (
          <>
            <div className="library-filter-search form-control-item">
              <input
                type="text"
                className="form-control"
                value={filters.search}
                onChange={(e) => onFilter(e.target.value, 'search')}
                placeholder="Search"
              />
              <i className="icon-search"></i>
            </div>
            <div>
              <button className="btn btn-outline" onClick={() => setFiltersIsOpen(!filtersIsOpen)}>
                <i className="icon-icon-sliders-rotate"></i>
              </button>
            </div>
          </>
        )}

        <div>
          <div className="dropdown" ref={ref}>
            <button onClick={() => setIsDropdownOpen(!isDropdownOpen)} className="btn">
              <i className="icon-plus-svgrepo-com mr-2"></i> Add
            </button>
            {isDropdownOpen && (
              <ul className="dropdown-menu">
                <li>
                  <button className="btn ml-0 btn-left btn-full btn-text" onClick={openAddModal}>
                    <i className="icon-plus-svgrepo-com mr-2"></i> Add manually
                  </button>
                </li>
                <li>
                  <button className={'btn ml-0 btn-full btn-text'} onClick={() => openImport(true)}>
                    <i className="icon-file-text mr-2"></i> Excel File import
                  </button>
                </li>
                <li>
                  <button className={'btn ml-0 btn-full btn-text'} onClick={() => openUpdateModal(true)}>
                    <i className="icon-file-text mr-2"></i> Excel File update
                  </button>
                </li>
                <li>
                  <button className={'btn ml-0 btn-full btn-text'} onClick={openAddAttachModal}>
                    <i className="icon-file-text mr-2"></i> Add from the submission
                  </button>
                </li>
              </ul>
            )}
          </div>
        </div>
        <button onClick={() => handlePrintUsers()} className="btn">
          Print
        </button>
      </div>
      {filtersIsOpen && (
        <div className="library-filters">
          <div className="library-filters-wrapper">
            <div className="form-item">
              <label className="form-label">Facility</label>
              <Select
                isMulti
                hideSelectedOptions={false}
                options={facilitiesOption}
                value={selectedFacilitiesOptions}
                onChange={(e) => onMultiSelectFilter(e, 'facilities_ids')}
                className="form-control-select"
                classNamePrefix="react-select-multi"
              ></Select>
            </div>
            <div className="form-item">
              <label className="form-label">Position</label>
              <Select
                isMulti
                options={positionsOption}
                hideSelectedOptions={false}
                value={selectedPositionsOptions}
                onChange={(e) => onMultiSelectFilter(e, 'positions_ids')}
                className="form-control-select"
                classNamePrefix="react-select-multi"
              ></Select>
            </div>
            <div className="form-item">
              <label className="form-label">User Group</label>
              <Select
                isMulti
                options={userGroupOption}
                hideSelectedOptions={false}
                value={selectedUserGroupsOptions}
                onChange={(e) => onMultiSelectFilter(e, 'user_groups_ids')}
                className="form-control-select"
                classNamePrefix="react-select-multi"
              ></Select>
            </div>
            <div className="form-item">
              <label className="form-label">Date added</label>
              <DateCalendar
                selectedDate={startDate}
                handleChange={handleDateChange}
                selectsRange
                startDate={startDate}
                endDate={endDate}
                isClearable
              />
            </div>
            <div className="form-item">
              <label className="form-label">Status</label>
              <Select
                options={userStatusOptions}
                value={userStatusOptions.find((option) => option.value === filters.status)}
                onChange={(e) => onSelectFilter(e, 'status')}
                className="form-control-select"
                classNamePrefix="react-select"
              ></Select>
            </div>
            {!isEmployee() && (
              <div className="form-item">
                <label className="form-label">System Role</label>
                <Select
                  hideSelectedOptions={false}
                  options={roleOptions}
                  value={selectedRoleOption}
                  onChange={(e) => onSelectFilter(e, 'role_ids')}
                  className="form-control-select"
                  classNamePrefix="react-select"
                ></Select>
              </div>
            )}
            <div className="form-item">
              <label className="form-label">Registration status</label>
              <Select
                hideSelectedOptions={false}
                options={userRegistrationStatusOptions}
                value={userRegistrationStatusOptions.find((option) => option.value === filters.registration_status)}
                onChange={(e) => onSelectFilter(e, 'registration_status')}
                className="form-control-select"
                classNamePrefix="react-select"
              ></Select>
            </div>
          </div>

          <button className="btn btn-outline" onClick={clearFilter}>
            Clear All
          </button>
        </div>
      )}

      {usersList?.length > 0 && (
        <div className="table-responsive table-default table-full">
          <div className="table-wrapper">
            <div className="table">
              <div className="tr">
                {column.map((item, index) => (
                  <div
                    className="th"
                    key={index}
                    style={{
                      ...(item.styles || {}),
                      ...(item.sortKey ? { cursor: 'pointer', userSelect: 'none' } : {}),
                    }}
                    onClick={item.sortKey ? () => toggleSort(item.sortKey) : undefined}
                  >
                    {item.name}
                    {item.sortKey && ordering === `-${item.sortKey}` && (
                      <>
                        {' '}
                        <svg
                          height={12}
                          width={12}
                          fill="#667085"
                          xmlns="http://www.w3.org/2000/svg"
                          shapeRendering="geometricPrecision"
                          textRendering="geometricPrecision"
                          imageRendering="optimizeQuality"
                          fillRule="evenodd"
                          clipRule="evenodd"
                          viewBox="0 0 404 511.51"
                        >
                          <path
                            fillRule="nonzero"
                            d="M184.29 442.88 184.83 0h34.95l-.54 438.53 158.99-173.49L404 288.61l-204.27 222.9L0 293.11l25.77-23.57z"
                          />
                        </svg>
                      </>
                    )}
                    {item.sortKey && ordering === item.sortKey && (
                      <>
                        {' '}
                        <svg
                          height={11}
                          width={11}
                          fill="#667085"
                          xmlns="http://www.w3.org/2000/svg"
                          shapeRendering="geometricPrecision"
                          textRendering="geometricPrecision"
                          imageRendering="optimizeQuality"
                          fillRule="evenodd"
                          clipRule="evenodd"
                          viewBox="0 0 404 511.5"
                        >
                          <path
                            fillRule="nonzero"
                            d="m219.24 72.97.54 438.53h-34.95l-.55-442.88L25.77 241.96 0 218.39 199.73 0 404 222.89l-25.77 23.58z"
                          />
                        </svg>
                      </>
                    )}
                    {item.sortKey && ordering !== item.sortKey && ordering !== `-${item.sortKey}` && (
                      <>
                        {' '}
                        <svg
                          height={12}
                          width={18}
                          fill="#667085"
                          xmlns="http://www.w3.org/2000/svg"
                          shapeRendering="geometricPrecision"
                          textRendering="geometricPrecision"
                          imageRendering="optimizeQuality"
                          fillRule="evenodd"
                          clipRule="evenodd"
                          viewBox="0 0 900 511.51"
                        >
                          <path
                            fillRule="nonzero"
                            d="m715.24 72.97.54 438.53h-34.95l-.55-442.88-158.51 172.34-25.77-23.57L695.73 0 900 222.89l-25.77 23.58zM184.29 442.88 184.83 0h34.95l-.54 438.53 158.99-173.49L404 288.61l-204.27 222.9L0 293.11l25.77-23.57z"
                          />
                        </svg>
                      </>
                    )}
                    {item.tooltip && (
                      <>
                        <i data-tooltip-id={`table-tooltip-${index}`} className="icon-icon-info"></i>
                        <Tooltip opacity={1} id={`table-tooltip-${index}`} place="bottom" content={item.tooltip} />
                      </>
                    )}
                    {!!item.total && <span className="th-total">(Total: {item.total} users)</span>}
                  </div>
                ))}
              </div>
              {usersList.map((item) => (
                <div
                  className={`tr cursor-pointer ${item.deleted_at ? 'tr-deactivated' : ''}`}
                  key={item.id}
                  onClick={() =>
                    navigate(
                      `/company/${id}/${location.pathname.includes('users') ? 'users' : 'user'}/${item.id}`,
                      // lets the profile point its breadcrumb back to this exact filtered list
                      filtersUser ? { state: { usersListSearch: location.search } } : undefined,
                    )
                  }
                >
                  {!isShortTable && (
                    <>
                      <div className="td">
                        <div className="d-flex">
                          {item.last_name} {item.first_name}
                        </div>
                      </div>
                      <div className="td">{item.facility?.name}</div>
                      <div className="td">{item.position?.name}</div>
                      <div className="td">{item.user_groups.map((item) => item.name).join(', ')}</div>
                      <div className="td" style={{ maxWidth: '150px', width: '150px' }}>
                        {utcToLocalDateFormatter(item.created_at)}
                      </div>
                      <div className="td" style={{ maxWidth: '150px', width: '150px' }}>
                        {utcToLocalDateFormatter(item.activated_at) || '-'}
                      </div>
                      <div className="td" style={{ maxWidth: '150px', width: '150px' }}>
                        {formatCalendarDate(item.hire_date) || '-'}
                      </div>
                    </>
                  )}
                  {isShortTable && (
                    <>
                      <div className="td">
                        <div className="d-flex">
                          {item.last_name} {item.first_name}
                        </div>
                      </div>
                      <div className="td">{item.position?.name}</div>
                      <div className="td">{item.user_groups.map((item) => item.name).join(', ')}</div>
                      <div className="td">{utcToLocalDateFormatter(item.date_added)}</div>
                    </>
                  )}
                </div>
              ))}
            </div>
            {totalPage > 1 && (
              <ReactPaginate
                className={'react-pagination'}
                breakLabel="..."
                nextLabel="next ->"
                pageRangeDisplayed={5}
                pageCount={totalPage}
                previousLabel="<- previous"
                forcePage={page ? page - 1 : undefined}
                onPageChange={handlePageClick}
              />
            )}
          </div>
        </div>
      )}

      {!usersList?.length && !search && (
        <EmptyPage
          imageUrl={'/images/empty-user.png'}
          title={'Currently, there are no registered users'}
          subtitle={'Simply click the button “Add User”  to initiate the user addition process'}
        />
      )}
      {!usersList?.length && search && (
        <EmptyPage
          imageUrl={'/images/no-resoult.png'}
          title={'Unfortunately, nothing was found for your query.'}
          subtitle={"Try to change the range of keywords. We're here to help you find what you're looking for."}
        />
      )}
    </>
  );
}

export default UserTab;
